// Baked default design system from oa-studio-prototype. The ONLY sanctioned
// hardcoded-color module (see spec §5). HSL tokens, no '#'.
export const blockTheme = {
  primary: '262 83% 58%', // == @teach/ui brand
  primaryFg: '0 0% 100%',
  background: '0 0% 100%', // light block canvas (content blocks render white)
  foreground: '240 10% 4%', // near-black text ON the light canvas
  muted: '240 5% 96%', // light muted surface (empty-state chips)
  success: '142 71% 45%',
  partial: '35 92% 50%',
  destructive: '0 84% 60%',
  radius: '0.75rem',
  quizCanvas: '240 13% 96.5%',
  quizText: '240 10% 12%',
  quizMuted: '240 5% 45%',
  quizIdleBorder: '240 6% 88%',
  quizOptionText: '258 13% 34%',
  quizCardBg: '0 0% 100%', // white quiz card surface on the grey canvas
  onMediaIcon: '0 0% 62%', // decorative icon over a full-bleed photo (e.g. PreviewShell close-X)
  // Типографика статьи (`type: "article"`). Значения взяты у ПЛЕЕРА
  // (academy-frontend `.../InstructionBlock/ArticleContent.module.css` +
  // `InstructionBlock.module.css`), а не у рефа: статью читатель видит в плеере,
  // и превью обязано показывать ровно её вёрстку. Как и `designBlock`, это
  // готовые CSS-значения, а не HSL-токены — `hsl()` к ним не применять.
  article: {
    heading: '#232323',
    text: '#464646',
    /** Акцент академии: ссылки. */
    accent: '#A66CFF',
    /** Маркер — как в плеере TMA (`.prose mark`): светлая сиреневая заливка и
     *  тёмный текст, подсветка маркером по бумаге. */
    markBg: '#D9C0FF',
    markText: '#232323',
  },
  // Экраны квизов (choice / yes_no / fill_blank / match) и нижняя CTA
  // урока. Палитра ПЛЕЕРА — academy-frontend `src/styles/colors.css`, переменные
  // `--block-*` («значения как в TMA»), 1:1 по имени. Как и `designBlock`, это
  // готовые CSS-значения (rgba, тени, градиенты) — `hsl()` к ним не применять.
  quiz: {
    /** Фон экрана вокруг сцены квиза в плеере (`NEUTRAL_BG`). В студии не используется:
     *  весь экран квиза — цвета сцены (`bg`), иначе по краям видна серая рамка. */
    neutralBg: '#f7f7f8',
    /** Сцена квиза (`--block-quiz-bg`) и подложка варианта (`--block-canvas`). */
    bg: '#fffefd',
    text: '#202020',
    optionText: '#4d4c4b',
    mutedText: '#6d6d78',
    idleBorder: 'rgba(32, 32, 32, 0.5)',
    idleShadow: '0 2px 6px rgba(32, 32, 32, 0.08)',
    primary: '#b389f2',
    primaryFg: '#ffffff',
    success: '#21c45d',
    destructive: '#ef4343',
    muted: '#f4f4f5',
    primaryInk: '#6a20d2',
    successInk: '#2f814f',
    destructiveInk: '#893334',
    selectedInk: '#4c3471',
    /** Кромка погашенной CTA и кнопки «Назад» — заметна и на белом фоне. */
    mutedInk: '#c2c1c9',
    primarySoft: '#dbc4ff',
    successSoft: '#c1f2d4',
    destructiveSoft: '#ff9b9c',
    selectedShadow: 'inset 0 3px 3px rgba(76, 52, 113, 0.3)',
    successShadow: '0 3px 3px #97c3a8',
    successPanel: '#d2fde3',
    destructivePanel: '#ffcacb',
    /** Объёмный блик CTA: свет сверху — светлее у верха, темнее у кромки. */
    gloss: 'linear-gradient(180deg, rgba(255, 255, 255, 0.22), rgba(0, 0, 0, 0.1))',
    successGradient: 'linear-gradient(180deg, #5fd88d, #1fb559)',
    destructiveGradient: 'linear-gradient(180deg, #ff8a8c, #ef595b)',
    /** Цвета собранных пар сопоставления (плеер `--block-pair-N`): заливка и
     *  текст; пара — по номеру термина, дальше шестой цвета повторяются. Не
     *  похожи на зелёный и красный вердикта. */
    pairs: [
      { bg: '#d4e5ff', ink: '#1f4f99' },
      { bg: '#ffe7b0', ink: '#7a5300' },
      { bg: '#ffd6ee', ink: '#8a2a63' },
      { bg: '#cdeff0', ink: '#1d6266' },
      { bg: '#e6d9ff', ink: '#4c3471' },
      { bg: '#ffdfcc', ink: '#8a3f12' },
    ],
    /** «Да / Нет» — круги из макета квизов Figma, как в мобилке
     *  (`quiz-block/styles.ts`, `YES_NO`): плоская заливка, белый знак, подпись
     *  под кругом цветом варианта; невыбранный после ответа — серый. */
    yesNo: {
      yes: '#52c27d',
      no: '#ef595b',
      off: '#d8d8d8',
      glyph: '#fffefd',
      offGlyph: '#7c7c7c',
      yesText: '#2f814f',
      noText: '#893334',
      offText: '#7c7c7c',
      shadow: '0 3px 3px rgba(0, 0, 0, 0.12), inset 0 3px 3px rgba(0, 0, 0, 0.12)',
    },
  },
  // Пустой видео-экран на холсте редактора (зона загрузки с маскотом-огоньком).
  // Цвета маскота — оранжевая палитра его же иллюстрации; готовые CSS-значения.
  videoDrop: {
    bg: '#FFF4EC',
    bgActive: '#FFDCC4',
    border: '#FFC7A6',
    borderActive: '#FF662C',
    accent: '#FF662C',
    accentText: '#FFFFFF',
    title: '#232323',
    muted: '#8A7A70',
  },
  // Дизайн-блок: эталонная дизайн-система (Figma «OA Studio App»). В отличие от
  // остальных полей blockTheme здесь НЕ HSL-токены, а готовые CSS-значения:
  // макет построен на градиентах, тенях и бордерах, которые в форму `H S% L%`
  // не укладываются. Помощник `hsl()` к designBlock не применяется.
  // Размеры пересчитаны с артборда 1654px на канвас 390px (делитель 4.24).
  designBlock: {
    text: '#202020',
    backdropLight: 'linear-gradient(90deg, #FFDBC1, #FFF1E7)',
    backdropDark: 'linear-gradient(90deg, #202020, #664E8B)',
    backdropPrimary: 'linear-gradient(90deg, #DBC4FF, #EDE2FF)',
    /** «Прозрачная» подложка — заливки нет, читается только по рамке. */
    backdropTransparentBorder: '#CC7E54',
    /** Внутренний блик прозрачной подложки: белый 25%, вниз ~1, размытие 2.8
     *  (SVG «Group 31»: 4 / 12 на холсте 1654). Без него подложка — плоская рамка. */
    backdropTransparentHighlight: 'inset 0 1px 2.8px rgba(255, 255, 255, 0.25)',
    backdropLightText: '#202020',
    backdropDarkText: '#FFFCF5',
    backdropPrimaryText: '#202020',
    backdropTransparentText: '#202020',
    /** Тень акцентной подложки: #4C3471 30%, вниз 2.8 / размытие 2.8 (12 / 12 в макете). */
    backdropShadow: '0 2.8px 2.8px rgba(76, 52, 113, 0.3)',
    backdropRadius: '7px',
    backdropTransparentRadius: '12px',
    accentOnLight: '#FF8645',
    accentOnDark: '#B889FF',
    accentOnPrimary: '#894DFF',
    markerOnLight: '#FFB68F',
    markerOnDark: '#DBC4FF',
    markerOnPrimary: '#FFFCF5',
    markerOnPlain: '#FFCFB5',
    /** Текст под маркером тёмный ВЕЗДЕ, включая тёмную подложку: заливки маркера
     *  светлые, и светлый текст на них не читается. */
    markerText: '#202020',
    badgeFill: '#FFCFB5',
    badgeInk: '#9E4E30',
    badgeRaised: 'linear-gradient(90deg, #FFE7DA, #FF9E6A)',
    /** Тень «стекла»: #9E4E30 30%, 0 / 1.65 / 1.65 (7 / 7 в макете). */
    badgeRaisedShadow: '0 1.65px 1.65px rgba(158, 78, 48, 0.3)',
    tableHeaderBg: '#FFB68F',
    tableRowBg: '#FFFCF5',
    tableRowAltBg: '#EDE2FF',
    tableBorder: '#202020',
    tablePlaceholder: '#B0A8BC',
  },
} as const

export const hsl = (token: string, alpha?: number): string =>
  alpha === undefined ? `hsl(${token})` : `hsl(${token} / ${alpha})`

// Darker shade of an HSL token, for a 3D raised edge / gradient border.
export const shadowToken = (token: string): string => {
  const m = token.match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/)
  if (!m) return token
  const h = Number(m[1])
  const s = Math.round(Number(m[2]) * 0.85)
  const l = Math.max(Number(m[3]) - 25, 20)
  return `${h} ${s}% ${l}%`
}
